import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils.js";

/** How long the announcement stays, matching `Notices`' own toast. */
const TOAST_MS = 5000;

/**
 * *"Your turn"* / *"Ana's turn"*, once, when a round opens.
 *
 * # Findings 3.6 and 3.6b, and they are one finding in two directions
 *
 * *"The active player gets no announcement of their turn"*, and symmetrically,
 * *"non-active players get no announcement either"*. The room was left to work
 * out whose turn it was from a sentence in a header that had already been there
 * for the previous round.
 *
 * # ⚠️ Derived, not published
 *
 * There is no `Notice` for this and there should not be: whose turn it is is
 * already on every snapshot, so an engine notice would be a **second copy of a
 * fact the client already holds** — and the one that goes stale when a client
 * reconnects mid-round and replays a notice stream it missed. This reads
 * `round_no`, fires on the change, and forgets.
 *
 * That also makes it correct for a spectator and for the television, which have
 * no side of their own and are told *"Ana's turn"* like everybody else.
 *
 * # It announces the round, not the phase
 *
 * Keyed on `round_no` alone. A phase change inside a round is not a new turn,
 * and a re-announcement every time the engine moved from `Listening` to
 * `Guessing` would be the interruption ADR 0026 warns is worse than the drawer.
 *
 * ⚠️ *Keyed* literally: the inner component is remounted per round rather than
 * resetting its own state from a prop. Adjusting state inside an effect
 * cascades a render, and ESLint says so.
 */
export function TurnToast({
  roundNo,
  yours,
  who,
  className,
}: {
  /** The round in flight — the announcement fires when this changes. */
  roundNo: number;
  /** Whether the turn is the viewer's own. */
  yours: boolean;
  /** Whose turn it is, for everybody it is not. `null` for a side that left. */
  who: string | null;
  className?: string;
}) {
  // **Keyed by the round**, which is what makes "announce once per turn" a
  // property of the tree rather than a state machine. Remounting is React's own
  // answer to *"reset this when that input changes"* — the same shape
  // [`PhaseTimer`](./PhaseTimer.js) uses, and it keeps the effect below a plain
  // subscription instead of a synchronous `setState` that cascades a render.
  return (
    <Announcement
      key={roundNo}
      yours={yours}
      who={who}
      {...(className === undefined ? {} : { className })}
    />
  );
}

function Announcement({
  yours,
  who,
  className,
}: {
  yours: boolean;
  who: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const [gone, setGone] = useState(false);

  useEffect(() => {
    // Armed once on mount and cleared once on unmount. ⚠️ **No dependency on the
    // snapshot** — that is finding 4.1's mistake, where an effect watching an
    // array that arrives fresh off the socket cleared its own pending timeout
    // several times a second and never armed another.
    const timer = setTimeout(() => {
      setGone(true);
    }, TOAST_MS);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (gone) {
    return null;
  }

  return (
    <p
      role="status"
      data-testid="turn-toast"
      className={cn(
        "pointer-events-none rounded-xl border px-4 py-3 text-center text-base font-semibold shadow-lg backdrop-blur",
        yours
          ? "border-flash-500/60 bg-flash-500/20 text-white"
          : "border-white/15 bg-stage-900/95 text-white/70",
        className,
      )}
    >
      {yours ? t("round.turn.yours") : t("round.turn.theirs", { who: who ?? "" })}
    </p>
  );
}
